import { useCallback, useSyncExternalStore } from 'react'

export type FeatureFlag = 'analytics' | 'rentalDashboard' | 'favoriteLocations'

const STORAGE_PREFIX = 'feature_'

const DEFAULT_VALUES: Record<FeatureFlag, boolean> = {
  analytics: true,
  rentalDashboard: true,
  favoriteLocations: true,
}

// Subscribe to storage events for cross-tab sync
const listeners = new Set<() => void>()

function subscribe(callback: () => void) {
  listeners.add(callback)

  const handleStorage = (e: StorageEvent) => {
    if (e.key?.startsWith(STORAGE_PREFIX)) {
      callback()
    }
  }
  window.addEventListener('storage', handleStorage)

  return () => {
    listeners.delete(callback)
    window.removeEventListener('storage', handleStorage)
  }
}

function notifyListeners() {
  listeners.forEach((cb) => cb())
}

function getSnapshot(flag: FeatureFlag): boolean {
  if (typeof window === 'undefined') return DEFAULT_VALUES[flag]
  const stored = localStorage.getItem(`${STORAGE_PREFIX}${flag}`)
  if (stored === null) return DEFAULT_VALUES[flag]
  return stored === 'true'
}

function getServerSnapshot(flag: FeatureFlag): boolean {
  return DEFAULT_VALUES[flag]
}

export function useFeatureFlag(flag: FeatureFlag) {
  const enabled = useSyncExternalStore(
    subscribe,
    () => getSnapshot(flag),
    () => getServerSnapshot(flag),
  )

  const setEnabled = useCallback(
    (value: boolean) => {
      localStorage.setItem(`${STORAGE_PREFIX}${flag}`, String(value))
      notifyListeners()
    },
    [flag],
  )

  return [enabled, setEnabled] as const
}
